import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { FormControlValidatorService } from 'src/app/components/shared/sharedServices/form-control-validator.service';
import { MuToastService } from 'src/app/components/shared/sharedServices/mu-toast.service';
import { FlagNotChanged, Flags } from 'src/app/components/shared/types/flags.type';
import { ObservableManager } from 'src/app/components/shared/types/observableManager.type';
import { sharedConstants } from 'src/environments/constants';
import { environment } from 'src/environments/environment';
import { authConstants } from '../../auth.constants';
import { AuthService } from '../../auth.service';
import { CompanyBillingFlags } from './auth-company-billing.flags';
import { MuUserService } from 'src/app/components/shared/sharedProtos/user/mu-user.service';
import { LayoutAuthService } from 'src/app/components/layout/pages/layout-auth/layout-auth.service';
import { Actions } from 'src/app/components/shared/types/actions.type';
import { AuthCompanyBillingActions } from './auth-company-billing.actions.enum';
import { AccountType } from 'src/app/components/shared/models/interface/accountType.enum';
import { MuExceptionCode } from 'src/app/components/shared/models/interface/muExceptionCode.enum';
import { ItemNumberToFilter } from '../../models/interface/itemNumberToFilter.interface';
import { MuDeliveryService } from 'src/app/components/shared/sharedProtos/delivery/mu-delivery.service';
import { LoginSharedComponent } from 'src/app/components/shared/sharedModules/auth/login-shared/login-shared.component';
import { SharedAuthValidator } from 'src/app/components/shared/sharedConstants/auth.validators';
import { MuLayoutService } from 'src/app/components/shared/sharedProtos/layout/mu-layout.service';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { PermissionMenuService } from 'src/app/components/shared/sharedModules/permission/permission-menu.service';
import { PermissionService } from 'src/app/components/shared/sharedModules/permission/permission.service';
import { MuCookieService } from 'src/app/components/shared/sharedServices/mu-cookie.service';
import { MuCryptService } from 'src/app/components/shared/sharedServices/mu-crypt.service';
import { StorageService } from 'src/app/components/shared/sharedServices/storage.service';
import { UserDataService } from 'src/app/components/shared/sharedServices/user-data.service';
import { UtilsService } from 'src/app/components/shared/sharedServices/utils.service';

@Component({
  selector: 'app-auth-company-billing',
  templateUrl: './auth-company-billing.component.html',
  styleUrls: ['./auth-company-billing.component.sass'],
})
export class AuthCompanyBillingComponent implements OnInit, OnDestroy {
  private observableManager = new ObservableManager();
  flagList = new Flags<CompanyBillingFlags>();
  tmpUser: { [key: string]: any } = {};
  companyCreated: boolean;
  companyBillingFlags = CompanyBillingFlags;
  formData: UntypedFormGroup;
  documentTypeList: ItemNumberToFilter[];
  country: number;
  cityList: ItemNumberToFilter[];
  segmentationList: ItemNumberToFilter[];
  toastLife: number;
  actionList: Actions<AuthCompanyBillingActions, any> = new Actions<AuthCompanyBillingActions, any>();
  actionListEnum = AuthCompanyBillingActions;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private validator: FormControlValidatorService,
    private muToastService: MuToastService,
    private authService: AuthService,
    private muUserService: MuUserService,
    private layoutAuthService: LayoutAuthService<CompanyBillingFlags>,
    private muDeliveryService: MuDeliveryService,
    private layoutService: MuLayoutService,
    private muCookieService: MuCookieService,
    private translateService: TranslateService,
    private utilsService: UtilsService,
    private permissionService: PermissionService,
    private router: Router,
    private route: ActivatedRoute,
    private muCryptService: MuCryptService<any>,
    private per: PermissionMenuService,
    private storageService: StorageService,
    private userDataService: UserDataService
  ) {
    this.tmpUser = JSON.parse((localStorage.getItem('mu-auth-company') || '{}') + '');
    this.country = parseInt(JSON.parse(localStorage.getItem('mu-auth-phone')).country + '', 10);
  }

  async ngOnInit(): Promise<void> {
    await this.initData();
    this.watchFlags();
  }

  ngOnDestroy(): void {
    this.observableManager.release();
    if (this.companyCreated) {
      localStorage.removeItem('mu-auth-data');
      localStorage.removeItem('mu-auth-company');
      localStorage.removeItem('mu-auth-person');
      localStorage.removeItem('mu-auth-phone');
      return environment.guardClause;
    }
    const user: { [key: string]: any } = {};
    Object.keys(this.formData.controls).forEach(key => {
      user[key] = this.formData.get(key).value;
    });
    localStorage.setItem('mu-auth-company', JSON.stringify(user));
  }

  async initData(): Promise<void> {
    this.actionList.on(AuthCompanyBillingActions.REGISTER, (_, __) => {
      this.register();
    });
    this.layoutAuthService.updateSettings({
      tab: 1,
      step: 2,
      title: 'titleBilling',
      flagList: this.flagList,
      showFlag: CompanyBillingFlags.SHOW_SUCCESS_MODAL,
      actionFlag: CompanyBillingFlags.ACTION_SUCCESS_MODAL,
      loaderFlag: CompanyBillingFlags.LOADING_SUCCESS_MODAL,
    });
    this.toastLife = environment.toastData.life;
    const sharedAuthValidator = new SharedAuthValidator(this.validator);

    this.formData = this.formBuilder.group({
      isInvalidForm: true,
      businessName: sharedAuthValidator.getCompanyName(''),
      companyDocumentType: sharedAuthValidator.getCompanyDocumentType(0),
      companyDocumentNumber: sharedAuthValidator.getCompanyDocumentNumber(this.isMX, ''),
      verificationCode: sharedAuthValidator.getVerificationCodeCreate(this.isMX, ''),
      companyPhone: sharedAuthValidator.getCompanyPhone(''),
      contactName: sharedAuthValidator.getContactName(''),
      city: sharedAuthValidator.getCityId(0),
      zipCode: sharedAuthValidator.getPostalCode(this.isMX, ''),
      segmentationRangeId: sharedAuthValidator.getSegmentationRange(0),
    });

    await this.getCityList();
    await this.getDocumentTypeList();
    await this.getSegmentationList();

    if (!this.tmpUser) {
      return environment.guardClause;
    }

    Object.keys(this.tmpUser).forEach(key => {
      const control = this.formData.get(key);
      if (!control || !this.tmpUser[key]) {
        return environment.guardClause;
      }
      this.formData.get(key).setValue(this.tmpUser[key]);
    });
  }

  /**
   * show zip code by cuontry
   * @returns
   */
  get isMX(): boolean {
    return [3].includes(this.country);
  }

  /**
   * get city list
   */
  async getCityList(): Promise<void> {
    try {
      this.cityList = (await this.muDeliveryService.getCityList(sharedConstants.statusToFilterCity, this.country)).cityFormatedList;
// eslint-disable-next-line no-empty
    } catch (e) {}
  }

  /**
   * load document type list
   */
  async getDocumentTypeList(): Promise<void> {
    try {
      this.documentTypeList = await this.muUserService.getDocumentTypeList(this.country, AccountType.COMPANY);
// eslint-disable-next-line no-empty
    } catch (e) {}
  }

  /**
   * get segmentation list
   */
  async getSegmentationList(): Promise<void> {
    try {
      this.segmentationList = await this.authService.getSegmentationList(environment.countries.find(x => x.id === this.country));
// eslint-disable-next-line no-empty
    } catch (e) {}
  }

  /**
   * method to register company
   */
  async register(): Promise<void> {
    this.flagList.executeIfFalse(CompanyBillingFlags.ACTION, async (test: FlagNotChanged) => {
      try {
        const user = JSON.parse(localStorage.getItem('mu-auth-data'));
        this.companyCreated = await this.authService.registerCompany({
          email: user?.email,
          password: user?.password,
          businessName: this.formData.get('businessName').value,
          documentType: this.formData.get('companyDocumentType').value,
          documentNumber: this.formData.get('companyDocumentNumber').value.replace(/ /g, ''),
          verificationCode: this.formData.get('verificationCode').value,
          phone: this.formData.get('companyPhone').value,
          cellPhone: JSON.parse(localStorage.getItem('mu-auth-phone'))?.clientPhone,
          cityId: this.formData.get('city').value,
          contactName: this.formData.get('contactName').value,
          roleId: authConstants.defaultCompanyRole,
          zipCode: this.formData.get('zipCode').value,
          segmentationId: this.formData.get('segmentationRangeId').value,
        });
        this.companyCreated = true;
        this.flagList.enable(CompanyBillingFlags.SHOW_SUCCESS_MODAL);
      } catch (e: any | MuExceptionCode) {
        this.muToastService.showSpecificToast({
          exception: e,
          key: 'toast-layout',
          severity: 'error',
          summary: 'toast.auth.error.register.title',
          detail: e.code === MuExceptionCode.COMMON ? e.message : 'toast.auth.error.register.text',
          life: this.toastLife,
        });
      }
    });
  }
  /**
   * watch flags
   */
  watchFlags(): void {
    this.observableManager.watch(this.flagList.valueChanges(CompanyBillingFlags.ACTION_SUCCESS_MODAL)).subscribe(async flag => {
      if (!flag || this.flagList.get(CompanyBillingFlags.LOADING_SUCCESS_MODAL)) {
        return environment.guardClause;
      }
      flag.toggle();
      const user = JSON.parse(localStorage.getItem('mu-auth-data'));
      const loginComponent = new LoginSharedComponent(
        this.layoutService,
        this.muCookieService,
        this.translateService,
        this.muUserService,
        this.formBuilder,
        this.utilsService,
        this.permissionService,
        this.validator,
        this.router,
        this.route,
        this.muCryptService,
        this.per,
        this.storageService,
        this.muDeliveryService,
        this.userDataService
      );
      this.flagList.enable(CompanyBillingFlags.LOADING_SUCCESS_MODAL);
      this.companyCreated = true;
      await loginComponent.signIn({ email: user.email, password: user.password }, true, true);
      localStorage.removeItem('mu-auth-data');
    });
  }
}
